import React from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import SEOHead from '../components/SEOHead';
import PublicPageShell from '../components/landing/PublicPageShell';
import { Shield, Lock, Eye, Database, Users, Mail, Globe, Clock } from 'lucide-react';

// Même titre de rubrique que le centre d'aide : filet fin, icône monochrome.
const Section = ({ icon: Icon, title, children }) => (
  <section className="landing-help-section">
    <h2>
      <Icon size={17} strokeWidth={1.75} aria-hidden="true" />
      {title}
    </h2>
    <div className="landing-help-body">{children}</div>
  </section>
);

const PrivacyPolicy = () => {
  const { language, t } = useLanguage();
  const isFr = language === 'fr';

  return (
    <PublicPageShell
      eyebrow={t('footerLegal')}
      title={t('privacyPolicy')}
      lede={isFr
        ? 'Comment FretManager collecte, utilise et protège vos données personnelles.'
        : 'How FretManager collects, uses and protects your personal data.'}
    >
      <SEOHead
        title={t('seoPrivacyTitle')}
        description={t('seoPrivacyDesc')}
        canonical="/privacy"
      />
      <div className="landing-prose" style={{ maxWidth: '820px' }}>

        <Section icon={Database} title={t('privacyDataCollected')}>
          <p>{isFr
            ? 'Nous collectons les données suivantes dans le cadre de notre service :'
            : 'We collect the following data as part of our service:'}
          </p>
          <ul className="list-none space-y-2 mt-2">
            {(isFr ? [
              'Informations d\'identité : nom, prénom, adresse email, numéro de téléphone',
              'Informations d\'entreprise : nom, type, pays d\'opération, adresses de siège',
              'Données d\'expédition : détails des colis, poids, descriptions, adresses de livraison',
              'Données de connexion : adresse IP, type de navigateur, horodatage des sessions',
              'Données financières : historique des transactions, montants, devises',
            ] : [
              'Identity information: name, email address, phone number',
              'Company information: name, type, operating countries, headquarters addresses',
              'Shipment data: package details, weight, descriptions, delivery addresses',
              'Connection data: IP address, browser type, session timestamps',
              'Financial data: transaction history, amounts, currencies',
            ]).map((item, i) => (
              <li key={i} className="flex items-start gap-2">
                <span className="mt-1.5 h-1.5 w-1.5 rounded-full shrink-0" style={{ background: 'hsl(var(--accent))' }} />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </Section>

        <Section icon={Eye} title={t('privacyDataUsage')}>
          <p>{isFr
            ? 'Vos données sont utilisées exclusivement pour :'
            : 'Your data is used exclusively for:'}
          </p>
          <ul className="list-none space-y-2 mt-2">
            {(isFr ? [
              'Fournir et améliorer nos services de gestion de fret',
              'Assurer le suivi en temps réel de vos expéditions',
              'Envoyer des notifications liées à vos colis (email, SMS)',
              'Gérer la facturation et les paiements',
              'Assurer la sécurité de votre compte et prévenir la fraude',
              'Respecter nos obligations légales et réglementaires',
            ] : [
              'Provide and improve our freight management services',
              'Enable real-time tracking of your shipments',
              'Send package-related notifications (email, SMS)',
              'Manage billing and payments',
              'Ensure account security and prevent fraud',
              'Comply with legal and regulatory obligations',
            ]).map((item, i) => (
              <li key={i} className="flex items-start gap-2">
                <span className="mt-1.5 h-1.5 w-1.5 rounded-full shrink-0" style={{ background: 'hsl(var(--accent))' }} />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </Section>

        <Section icon={Lock} title={t('privacyDataProtection')}>
          <p>{isFr
            ? 'Nous mettons en place les mesures de sécurité suivantes :'
            : 'We implement the following security measures:'}
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
            {(isFr ? [
              { title: 'Chiffrement', desc: 'Toutes les données sont chiffrées en transit (TLS) et au repos' },
              { title: 'Authentification', desc: 'Accès sécurisé par JWT avec expiration automatique' },
              { title: 'Accès restreint', desc: 'Principe du moindre privilège pour tous les employés' },
              { title: 'Audit', desc: 'Journalisation complète des accès et modifications' },
            ] : [
              { title: 'Encryption', desc: 'All data encrypted in transit (TLS) and at rest' },
              { title: 'Authentication', desc: 'Secure JWT-based access with automatic expiration' },
              { title: 'Restricted access', desc: 'Least privilege principle for all employees' },
              { title: 'Audit', desc: 'Complete logging of access and modifications' },
            ]).map((item, i) => (
              <div key={i} className="rounded-lg border p-3" style={{ borderColor: 'hsl(var(--border))', background: 'hsl(var(--muted))' }}>
                <p className="font-semibold text-sm" style={{ color: 'hsl(var(--foreground))' }}>{item.title}</p>
                <p className="text-xs mt-1">{item.desc}</p>
              </div>
            ))}
          </div>
        </Section>

        <Section icon={Users} title={t('privacyDataSharing')}>
          <p>{isFr
            ? 'Nous ne vendons jamais vos données personnelles. Vos données peuvent être partagées uniquement avec :'
            : 'We never sell your personal data. Your data may only be shared with:'}
          </p>
          <ul className="list-none space-y-2 mt-2">
            {(isFr ? [
              'Les partenaires de livraison impliqués dans le transport de vos colis',
              'Les prestataires techniques nécessaires au fonctionnement du service (hébergement, email)',
              'Les autorités compétentes lorsque la loi l\'exige',
            ] : [
              'Delivery partners involved in transporting your packages',
              'Technical providers necessary for service operation (hosting, email)',
              'Competent authorities when required by law',
            ]).map((item, i) => (
              <li key={i} className="flex items-start gap-2">
                <span className="mt-1.5 h-1.5 w-1.5 rounded-full shrink-0" style={{ background: 'hsl(var(--accent))' }} />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </Section>

        <Section icon={Clock} title={t('privacyDataRetention')}>
          <p>{isFr
            ? 'Nous conservons vos données pendant la durée strictement nécessaire :'
            : 'We retain your data only as long as strictly necessary:'}
          </p>
          <ul className="list-none space-y-2 mt-2">
            {(isFr ? [
              'Données de compte : pendant toute la durée de votre inscription, puis 3 ans après suppression',
              'Données d\'expédition : 5 ans conformément aux obligations légales de traçabilité',
              'Journaux de connexion : 12 mois',
              'Données financières : 10 ans (obligations comptables)',
            ] : [
              'Account data: for the duration of your registration, then 3 years after deletion',
              'Shipment data: 5 years in accordance with traceability legal obligations',
              'Connection logs: 12 months',
              'Financial data: 10 years (accounting obligations)',
            ]).map((item, i) => (
              <li key={i} className="flex items-start gap-2">
                <span className="mt-1.5 h-1.5 w-1.5 rounded-full shrink-0" style={{ background: 'hsl(var(--accent))' }} />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </Section>

        <Section icon={Globe} title={t('privacyYourRights')}>
          <p>{isFr
            ? 'Conformément au RGPD et aux lois applicables, vous disposez des droits suivants :'
            : 'In accordance with GDPR and applicable laws, you have the following rights:'}
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 mt-3">
            {(isFr ? [
              { title: 'Accès', desc: 'Obtenir une copie de vos données' },
              { title: 'Rectification', desc: 'Corriger vos données inexactes' },
              { title: 'Suppression', desc: 'Demander l\'effacement de vos données' },
              { title: 'Portabilité', desc: 'Recevoir vos données dans un format standard' },
              { title: 'Opposition', desc: 'Vous opposer au traitement de vos données' },
              { title: 'Limitation', desc: 'Restreindre le traitement de vos données' },
            ] : [
              { title: 'Access', desc: 'Obtain a copy of your data' },
              { title: 'Rectification', desc: 'Correct inaccurate data' },
              { title: 'Erasure', desc: 'Request deletion of your data' },
              { title: 'Portability', desc: 'Receive your data in a standard format' },
              { title: 'Objection', desc: 'Object to the processing of your data' },
              { title: 'Restriction', desc: 'Restrict the processing of your data' },
            ]).map((item, i) => (
              <div key={i} className="rounded-lg border p-3 text-center" style={{ borderColor: 'hsl(var(--border))', background: 'hsl(var(--muted))' }}>
                <p className="font-semibold text-sm" style={{ color: 'hsl(var(--foreground))' }}>{item.title}</p>
                <p className="text-xs mt-1">{item.desc}</p>
              </div>
            ))}
          </div>
        </Section>

        <Section icon={Mail} title={t('privacyContact')}>
          <p>{isFr
            ? 'Pour toute question concernant cette politique ou pour exercer vos droits, contactez-nous :'
            : 'For any questions about this policy or to exercise your rights, contact us:'}
          </p>
          <div className="mt-3 rounded-xl border p-4" style={{ borderColor: 'hsl(var(--accent) / 0.3)', background: 'hsl(var(--accent) / 0.05)' }}>
            <p className="font-semibold" style={{ color: 'hsl(var(--foreground))' }}>FretManager — {t('dataProtection')}</p>
            <p className="mt-1">Email : privacy@fretmanager.com</p>
            <p>{t('responseTime30d')}</p>
          </div>
        </Section>

        {/* Back button */}
      </div>
    </PublicPageShell>
  );
};

export default PrivacyPolicy;
